/**
 * "What Flight Line shows" for a Both piece: the public summary the section page carries in place
 * of the article, then the fixed box that sends readers on to the full piece.
 */
import { useState } from 'react'
import { GrowingTextarea } from './Headline'
import { summaryErrorText } from './pieceHelpers'
import { btn, btnPrimary, errorText, focus } from './ui'

export function SummaryEditor({ summary, editable, live, canWrite, sectionLabel, onChange, draft, onDraft, onSave, onWrite }: {
  /** The summary on screen: typed or saved. Paragraphs are separated by blank lines. */
  summary: string
  editable: boolean
  /** Write the summary / Write it again is offered. */
  canWrite: boolean
  /** The piece is live: the summary is on the public page now, so edits wait for Save summary
   *  rather than autosaving half-typed text onto it. */
  live: boolean
  sectionLabel: string
  /** Autosaves (not live). */
  onChange: (v: string) => void
  /** Live only: the text being edited, or null when it is the saved summary. Held by the page so
   *  it survives switching the preview and counts as unsaved work when leaving. */
  draft: string | null
  onDraft: (v: string | null) => void
  /** Saves at once, once (live); rejects with the reason. Resolves with a note to show in place
   *  of "Saved", when the server has one (e.g. it reaches the section at the next sync). */
  onSave: (v: string) => Promise<string | null | void>
  onWrite: () => Promise<unknown>
}) {
  const [writing, setWriting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saveFailed, setSaveFailed] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [savedNote, setSavedNote] = useState<string | null>(null)
  const text = live ? (draft ?? summary) : summary
  const has = text.trim() !== ''
  const save = async () => {
    if (draft === null) return
    setSaving(true)
    setError(null)
    setSaveFailed(false)
    try {
      const note = await onSave(draft)
      onDraft(null)
      setSavedNote(note || null)
      setSaved(true)
    } catch (e) {
      setError(summaryErrorText(e))
      setSaveFailed(true)
    } finally {
      setSaving(false)
    }
  }
  const write = async () => {
    setWriting(true)
    setError(null)
    try {
      await onWrite()
      onDraft(null)
      setSaved(false)
      setSaveFailed(false)
    } catch (e) { setError(summaryErrorText(e)) } finally { setWriting(false) }
  }
  return (
    <div className="flex flex-col gap-3">
      {!has && !editable && <p className="m-0 font-ui text-[14px] text-paper-dim">No summary yet.</p>}
      {(has || editable) && (
        <label className="flex flex-col gap-1.5">
          <span className="sr-only">{sectionLabel} summary</span>
          <GrowingTextarea
            value={text} readOnly={!editable} rows={4}
            placeholder="No summary yet."
            onChange={(e) => {
              if (live) { onDraft(e.target.value); setSaved(false) } else onChange(e.target.value)
            }}
            className={`min-h-[140px] w-full resize-none rounded border border-transparent bg-transparent p-0 font-serif text-[19px] leading-[1.65] text-paper placeholder:text-paper-faint hover:border-ink-500 ${focus}`}
          />
          {editable && <span className="font-ui text-[12px] text-paper-faint">Leave a blank line between paragraphs.</span>}
        </label>
      )}
      {live && editable && (
        <div className="flex flex-wrap items-center gap-2 font-ui">
          <button type="button" className={btnPrimary} disabled={draft === null || draft === summary || saving} onClick={() => void save()}>
            {saving ? 'Saving…' : 'Save summary'}
          </button>
          {saved && draft === null && (savedNote
            ? <span role="status" className="text-[13px] font-semibold text-paper">{savedNote}</span>
            : <span role="status" className="text-[13px] font-semibold text-radar-green">Saved to {sectionLabel}</span>)}
          {draft !== null && draft !== summary && <span className="text-[12px] text-paper-faint">The live page changes when you save.</span>}
        </div>
      )}
      {canWrite && (
        <div className="flex flex-wrap items-center gap-2 font-ui">
          <button type="button" className={btn} disabled={writing} onClick={() => void write()}>
            {writing ? 'Writing…' : has ? 'Write it again' : 'Write the summary'}
          </button>
          {has && !writing && <span className="text-[12px] text-paper-faint">Replaces the text above.</span>}
        </div>
      )}
      {error && (
        <div role="alert" className="flex flex-wrap items-center gap-2 font-ui">
          <p className={errorText}>{error}</p>
          {saveFailed && draft !== null && (
            <button type="button" className={btn} disabled={saving} onClick={() => void save()}>Try again</button>
          )}
        </div>
      )}
      {/* As the reader site closes a summary ([section]/[slug].astro, digest-notice-foot). */}
      <aside className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-radar-cyan/30 bg-radar-cyan/10 px-5 py-[18px] font-ui">
        <span className="flex-[1_1_260px] text-[15px] text-paper">
          That is the summary. The full article, with all its photographs, is in the member archive.
        </span>
        <span className="inline-flex min-h-10 items-center rounded-lg bg-ink-900 px-4 text-[14px] font-semibold text-paper">Read the full article →</span>
      </aside>
    </div>
  )
}
